import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

// Enviar teste a partir da linha do template: o número de quem testa e os
// valores das variáveis, sem montar JSON nem abrir o Playground. Os campos saem
// do próprio template — variáveis do corpo pelo nome, código no de Autenticação,
// o final do link e o cupom dos botões que pedem.

interface Btn { type: string; text?: string; url?: string; example?: string }
export interface TestTemplate {
  name: string; language: string; category: string; variables: number;
  spec?: {
    body?: { text?: string }; buttons?: Btn[]; header?: { type?: string };
    limited_time_offer?: { has_expiration?: boolean };
    cards?: { body?: { text?: string }; buttons?: Btn[] }[];
  };
}

const VAR_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

function bodyNames(text: string): string[] {
  const seen: string[] = [];
  for (const m of text.matchAll(VAR_RE)) if (!seen.includes(m[1])) seen.push(m[1]);
  return seen;
}

export function TestSendDialog({ tpl, onOpenChange }: { tpl: TestTemplate | null; onOpenChange: (v: boolean) => void }) {
  const t = useT();
  const [to, setTo] = useState('');
  const [vars, setVars] = useState<string[]>([]);
  const [buttons, setButtons] = useState<Record<number, string>>({});
  const [code, setCode] = useState('');
  const [loc, setLoc] = useState({ lat: '', lng: '', name: '', address: '' });
  const [expires, setExpires] = useState('');
  const [cardVals, setCardVals] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  if (!tpl) return null;

  const isAuth = tpl.category === 'AUTHENTICATION';
  const names = bodyNames(tpl.spec?.body?.text ?? '').slice(0, tpl.variables);
  // Botões que pedem valor no envio: link com variável e cupom sem exemplo fixo.
  const btnNeeds = (tpl.spec?.buttons ?? [])
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => (b.type === 'url' && (b.url ?? '').includes('{{')) || (b.type === 'copy_code' && !b.example));

  const isLocation = tpl.spec?.header?.type === 'location';
  const needsExpiry = !!tpl.spec?.limited_time_offer?.has_expiration;
  // Carrossel: o que cada cartão pede (variáveis do texto e link variável).
  const cardNeeds = (tpl.spec?.cards ?? []).map((c, ci) => ({
    ci,
    vars: bodyNames(c.body?.text ?? ''),
    urls: (c.buttons ?? []).map((b, bi) => ({ b, bi })).filter(({ b }) => b.type === 'url' && (b.url ?? '').includes('{{')),
  }));
  const cardKeys = cardNeeds.flatMap((c) => [...c.vars.map((_, k) => `${c.ci}:v${k}`), ...c.urls.map((u) => `${c.ci}:b${u.bi}`)]);
  const ready = to.trim() !== '' && (!isLocation || (loc.lat.trim() !== '' && loc.lng.trim() !== ''))
    && (!needsExpiry || expires !== '') && cardKeys.every((k) => (cardVals[k] ?? '').trim() !== '') && (isAuth
    ? code.trim() !== ''
    : names.every((_, i) => (vars[i] ?? '').trim() !== '') && btnNeeds.every(({ i }) => (buttons[i] ?? '').trim() !== ''));

  function close() {
    onOpenChange(false);
    setVars([]); setButtons({}); setCode(''); setLoc({ lat: '', lng: '', name: '', address: '' });
    setExpires(''); setCardVals({});
  }

  async function send() {
    if (!tpl || !ready) return;
    setSending(true);
    try {
      const body: Record<string, unknown> = { to: to.trim(), template: tpl.name, language: tpl.language };
      if (isAuth) body.code = code.trim();
      else {
        if (names.length) body.variables = names.map((_, i) => vars[i].trim());
        if (needsExpiry) body.offer_expires_at = new Date(expires).toISOString();
        if (cardNeeds.length) {
          body.cards = (tpl.spec?.cards ?? []).map((c, ci) => ({
            variables: bodyNames(c.body?.text ?? '').map((_, k) => (cardVals[`${ci}:v${k}`] ?? '').trim()),
            button_variables: (c.buttons ?? []).map((_, bi) => (cardVals[`${ci}:b${bi}`] ?? '').trim()),
          }));
        }
        if (isLocation) body.header_variables = [loc.lat.trim(), loc.lng.trim(), loc.name.trim(), loc.address.trim()];
        if (btnNeeds.length) {
          const all = (tpl.spec?.buttons ?? []).map((_, i) => (buttons[i] ?? '').trim());
          body.button_variables = all;
        }
      }
      const r = await apiFetch('POST', '/messages/template', body);
      if (!r.ok) {
        toast.error((r.data as { message?: string } | null)?.message ?? t('templates.test.failed'));
        return;
      }
      toast.success(t('templates.test.sent', { to: to.trim() }));
      close();
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => (v ? undefined : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('templates.test.title', { name: tpl.name })}</DialogTitle>
          <DialogDescription>{t('templates.test.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <Label htmlFor="test-to">{t('templates.test.to')}</Label>
            <Input id="test-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+5511999999999" inputMode="tel" />
          </div>
          {isLocation && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="test-lat">{t('templates.test.latitude')}</Label>
                <Input id="test-lat" value={loc.lat} onChange={(e) => setLoc({ ...loc, lat: e.target.value })} placeholder="-23.5614" />
              </div>
              <div>
                <Label htmlFor="test-lng">{t('templates.test.longitude')}</Label>
                <Input id="test-lng" value={loc.lng} onChange={(e) => setLoc({ ...loc, lng: e.target.value })} placeholder="-46.6559" />
              </div>
              <div>
                <Label htmlFor="test-place">{t('templates.test.place')}</Label>
                <Input id="test-place" value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="test-address">{t('templates.test.address')}</Label>
                <Input id="test-address" value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} />
              </div>
            </div>
          )}
          {isAuth ? (
            <div>
              <Label htmlFor="test-code">{t('templates.test.code')}</Label>
              <Input id="test-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
            </div>
          ) : names.map((n, i) => (
            <div key={n}>
              <Label htmlFor={`test-var-${i}`}>{t('templates.test.variable', { name: n })}</Label>
              <Input id={`test-var-${i}`} value={vars[i] ?? ''}
                onChange={(e) => { const next = [...vars]; next[i] = e.target.value; setVars(next); }} />
            </div>
          ))}
          {!isAuth && btnNeeds.map(({ b, i }) => (
            <div key={i}>
              <Label htmlFor={`test-btn-${i}`}>
                {b.type === 'url' ? t('templates.test.urlSuffix', { button: b.text ?? '' }) : t('templates.test.coupon', { button: b.text ?? '' })}
              </Label>
              <Input id={`test-btn-${i}`} value={buttons[i] ?? ''} onChange={(e) => setButtons({ ...buttons, [i]: e.target.value })} />
            </div>
          ))}
          {needsExpiry && (
            <div>
              <Label htmlFor="test-exp">{t('templates.rich.testOffer')}</Label>
              <Input id="test-exp" type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </div>
          )}
          {cardNeeds.filter((c) => c.vars.length || c.urls.length).map((c) => (
            <div key={c.ci} className="flex flex-col gap-2 rounded-md border p-2">
              <span className="text-xs font-medium">{t('templates.rich.testCard', { n: c.ci + 1 })}</span>
              {c.vars.map((n, k) => (
                <Input key={n} aria-label={t('templates.test.variable', { name: n })} placeholder={t('templates.test.variable', { name: n })}
                  value={cardVals[`${c.ci}:v${k}`] ?? ''} onChange={(e) => setCardVals({ ...cardVals, [`${c.ci}:v${k}`]: e.target.value })} />
              ))}
              {c.urls.map(({ b, bi }) => (
                <Input key={bi} aria-label={t('templates.test.urlSuffix', { button: b.text ?? '' })} placeholder={t('templates.test.urlSuffix', { button: b.text ?? '' })}
                  value={cardVals[`${c.ci}:b${bi}`] ?? ''} onChange={(e) => setCardVals({ ...cardVals, [`${c.ci}:b${bi}`]: e.target.value })} />
              ))}
            </div>
          ))}
          <p className="rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground">{t('templates.test.cost')}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common.cancel')}</Button>
          <Button onClick={send} disabled={!ready || sending}>
            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}{t('templates.test.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
